import { Image, ImageCategory } from '../types';
import { imageCache } from '../utils/imageCache';
import { USE_BACKEND_API } from '../config/apiConfig';
import { backendImageService } from './backendImageService';
import { supabase } from './supabaseService';

class ImageService {
  private cache: Map<string, Image[]> = new Map();
  private cacheExpiry: Map<string, number> = new Map();
  private readonly CACHE_DURATION = 4 * 60 * 60 * 1000;
  private allImagesCache: Image[] | null = null;
  private allImagesCacheTimestamp: number = 0;

  // Clear cache
  private clearCache(category?: string) {
    if (category) {
      this.cache.delete(category);
      this.cacheExpiry.delete(category);
    } else {
      this.cache.clear();
      this.cacheExpiry.clear();
      this.allImagesCache = null;
      this.allImagesCacheTimestamp = 0;
    }
  }

  // Get all images
  async getAllImages(forceRefresh: boolean = false): Promise<Image[]> {
    if (USE_BACKEND_API) {
      return await backendImageService.getAllImages(false, forceRefresh);
    }

    // Supabase path
    if (!forceRefresh && this.allImagesCache && (Date.now() - this.allImagesCacheTimestamp) < this.CACHE_DURATION) {
      return [...this.allImagesCache];
    }

    return await this.fetchAllImagesFromSupabase();
  }

  private async fetchAllImagesFromSupabase(): Promise<Image[]> {
    try {
      const { data, error } = await supabase
        .from('images')
        .select('*')
        .eq('active', true)
        .order('sort_order', { ascending: true });

      if (error) throw error;

      const images = await Promise.all((data || []).map((img: any) => this.mapSupabaseImage(img)));
      this.allImagesCache = images;
      this.allImagesCacheTimestamp = Date.now();
      return images;
    } catch (error) {
      console.error('Error fetching images from Supabase:', error);
      return [];
    }
  }

  // Get images by category
  async getImagesByCategory(category: string): Promise<Image[]> {
    if (USE_BACKEND_API) {
      return await backendImageService.getImagesByCategory(category);
    }

    // Supabase path
    try {
      const { data, error } = await supabase
        .from('images')
        .select('*')
        .eq('category', category)
        .eq('active', true);

      if (error) throw error;
      return await Promise.all((data || []).map((img: any) => this.mapSupabaseImage(img)));
    } catch (error) {
      console.error('Error fetching images by category:', error);
      return [];
    }
  }

  async getFeaturedImagesByCategory(category: string): Promise<Image[]> {
    const images = await this.getImagesByCategory(category);
    return images.filter(img => img.featured);
  }

  async getImageCategories(): Promise<ImageCategory[]> {
    // This could also be fetched from backend if needed
    return [
      { id: 'team', name: 'Team Photos', description: 'Photos of team members', images: [] },
      { id: 'hero', name: 'Hero Images', description: 'Banner images', images: [] },
      { id: 'conservation', name: 'Conservation', description: 'Conservation images', images: [] },
      { id: 'community', name: 'Community', description: 'Community photos', images: [] }
    ];
  }

  private async mapSupabaseImage(dbImage: any): Promise<Image> {
    const cachedUrl = await imageCache.getCachedImageUrl(dbImage.url);
    return {
      id: dbImage.id,
      category: dbImage.category,
      name: dbImage.name,
      description: dbImage.description || '',
      url: cachedUrl,
      altText: dbImage.alt_text || '',
      fileSize: dbImage.file_size || 0,
      mimeType: dbImage.mime_type || '',
      width: dbImage.width || 0,
      height: dbImage.height || 0,
      active: dbImage.active !== false,
      featured: dbImage.featured === true,
      sortOrder: dbImage.sort_order || 0,
      createdAt: dbImage.created_at || new Date().toISOString(),
      updatedAt: dbImage.updated_at || new Date().toISOString()
    };
  }

  async addImage(imageData: any): Promise<Image | null> {
    if (USE_BACKEND_API) {
      return await backendImageService.addImage(imageData);
    }
    // Implement Supabase add if needed...
    return null;
  }

  async updateImage(id: string, imageData: any): Promise<Image | null> {
    if (USE_BACKEND_API) {
      return await backendImageService.updateImage(id, imageData);
    }
    return null;
  }

  async deleteImage(id: string): Promise<boolean> {
    if (USE_BACKEND_API) {
      return await backendImageService.deleteImage(id);
    }
    return false;
  }

  async toggleImageActive(id: string): Promise<boolean> {
    if (USE_BACKEND_API) {
      return await backendImageService.toggleImageActive(id);
    }
    return false;
  }

  async toggleImageFeatured(id: string): Promise<boolean> {
    if (USE_BACKEND_API) {
      return await backendImageService.toggleImageFeatured(id);
    }
    return false;
  }
}

export const imageService = new ImageService();
export default imageService;
